import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, mergeMap, catchError } from "rxjs/operators";
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { Work } from '../models/work.model';
import { JobPai } from '../models/job-pai.model';
import { JobOS } from '../models/job-os.model';
import { FileService } from './file.service';
import { CacheService } from './cache.service';
import { ToastrService } from './toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { AuthService } from './auth.service';


@Injectable({
  providedIn: 'root'
})
export class JobsService {

  constructor(private http: HttpClient, @Inject("environment") private environment: any, private fileService: FileService,
    private cacheService: CacheService, private toastService: ToastrService, private translateService: TranslateService,
    private authService: AuthService, private router:Router) { }

  className = 'JobsService';

  getJobById(workId): Observable<Work> {
    return this.getJobs(workId).pipe(map(d => {
      let job = _.find(d, work => {
        return workId == _.get(work, 'idJobPk');
      });
      job = this.setUnitBusinessToAllJobProcess(job);
      _.forEach(job.organizationalStructure, os => {
        if(!os.jobExpected){
          os.jobExpected = 0 // mejora previsto / por defecto si el campo jobExpected de eo esta null (no existe / es antiguo -> no tiene) se ponga a true por defecto
        }
      })
      return job;
    }));
  }

  getJobStatistics(params) {
    return this.http.get<any[]>(this.environment.base_URL + "/job-statistics", { params });
  }

  /**
   * Export jobs
   * @param key 
   * @param params 
   */
  exportJob(key, params, data = null) {

    _.set(params, "key", key + '/data.json');

    // Call aws gateway to generate pre signed url
    return this.http.get<any[]>(this.environment.base_URL + "/exported-url", { params })
      .pipe(mergeMap((res1: any) => {

        // Remove key paramenter and add url parameter
        _.unset(params, "key")
        _.set(params, "url", res1)

        // Call Mapgre Gateway to generate excel with exoprted jobs
        return this.http.get<any[]>(this.environment.base_URL + "/exported-jobs", { params })
          .pipe(mergeMap((res2: any) => {

            // Remove url paramenter and add key parameter
            let paramsExcel = {}
            _.set(paramsExcel, "keydata", key + '/data.json');
            _.set(paramsExcel, "keyexcel", key + '/export.xlsx');


            const section = _.get(_.keys(data), '[0]');
            let method = 'get';
            let url = '/create-excel';

            if (section === 'following') {
              url = '/create-excel-following';
              _.set(paramsExcel, 'data', data);
              method = 'post';
            }

            return this.http[method]<any[]>(this.environment.base_URL + url, { params: paramsExcel })
              .pipe(mergeMap((res3: any) => {

                // Remove url paramenter and add key parameter
                let paramsDownload = {}
                _.set(paramsDownload, "onlyDownload", true)

                let body = {
                  key: key + '/export.xlsx'
                }

                // Download excel
                return this.http.post(this.environment.base_URL + `/download`, body, { params: paramsDownload })                
                  .pipe(mergeMap((res4: any) => {

                    // Download file
                    let options = {
                      responseType: "blob" as 'json'
                    }

                    // Download file form S3
                    return this.http.get(res4, options);

                  }), map(res5 => {

                    // Delete file when downloaded
                    this.delete(key + '/export.xlsx').subscribe(() => { });
                    return res5;

                  }), catchError(err => {
                    this.toastService.add({
                      severity: "error",
                      summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                      detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                    });
                    return throwError(err);
                  }))


              }))
          }))

      }), map(res6 => {
        return res6;
      }), catchError(err => {
        this.toastService.add({
          severity: "error",
          summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
          detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
        });
        return throwError(err);
      }))


  }

  delete(key): any {
    let params = {
      key
    }
    return this.http.post(this.environment.base_URL + "/delete", { params }).pipe(catchError(err => {
      this.toastService.add({
        severity: "error",
        summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
        detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
      });
      return throwError(err);
    }));
  }

  deleteJob(id: Number): Observable<any> {
    return this.http.delete(this.environment.base_URL + `/jobs/${id}`)
  }

  getTypeJobState(cache = true, cacheKey = "jobState"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/state").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobExpected() {
    return of([
      { name: 'FILTERS.YES', value: 0 },
      { name: 'FILTERS.NO', value: 1 }
    ]);
  }

  getMixedTeam(): Observable<any[]> {
    return of([
      { name: 'FILTERS.YES', value: 1 },
      { name: 'FILTERS.NO', value: 0 }
    ]);
  }

  getTypeJobOrigin(cache = true, cacheKey = "jobOrigin"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/origin").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }
  getTypeJob(cache = true, cacheKey = "jobType"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/type").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }
  getReasonCloseJob(cache = true, cacheKey = "jobReasonClose"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/reason-close").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getUsersByJob(workId = null, cache?, cacheKey = "usersByJob"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + `/jobs/${workId}/users`).pipe(map((res) => {
        res = _.uniqBy(res, item => item.idUserPk);
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getMateriality(cache = true, cacheKey = "jobMateriality"): Observable<any[]> {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable =  this.http.get<any[]>(this.environment.base_URL + "/jobs/materiality").pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobs(workId = null): Observable<any[]> {
    let params: any = {}
    if (workId) {
      params.jobId = workId;
    }

    return this.http.get<any[]>(this.environment.base_URL + "/jobs", { params });
  }

  addJob(job): Observable<any[]> {
    return this.http.post<any[]>(this.environment.base_URL + "/jobs", job);
  }

  addJobWithFiles(job): Observable<any[]> {
    // PRE FILES INI
    let filesToUpload = _.remove(_.get(job, 'jobDocuments'), (f) => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.addJob(job).pipe(mergeMap((res: any) => {
        let observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'job', res));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.addJob(job)
    }
  }

  addJobListWithFiles(jobs): Observable<any[]> {
    let observables: Observable<any[]>[] = _.map(jobs, (job) => this.addJobWithFiles(job));
    return forkJoin(observables);
  }

  getProgramByWorkID(workId) {
    return this.http.get(this.environment.base_URL + `/program-job/${workId}`);
  }

  getProgramTemplates() {
    return this.http.get(this.environment.base_URL + `/guide-job`);
  }

  deleteGuideJob(workId) {
    return this.http.delete(this.environment.base_URL + `/guide-job/${workId}`);
  }

  saveProgramJob(program, jobId?) {
    let params: any = {};
    if (jobId) {
      params.jobId = jobId;
    }
    return this.http.post(this.environment.base_URL + `/program-job`, program, { params });
  }

  saveGuideJob(guide) {
    return this.http.post(this.environment.base_URL + `/guide-job`, guide);
  }

  changeState(job) {
    return this.http.post(this.environment.base_URL + `/jobs/update-state`, job);
  }

  setUnitBusinessToAllJobProcess(job) {
    _.forEach(_.get(job, 'jobProcess'), (jp) => {
      if (!_.get(jp, 'unitBusiness')) {
        jp.unitBusiness = this.findUnitBusinessInJobProcess(jp);
      }
    })
    return job;
  }

  findUnitBusinessInJobProcess(jp) {
    if (_.get(jp, 'processUnitBusiness')) {
      return _.get(jp, 'processUnitBusiness.unitBusiness');
    } else if (_.get(jp, 'processCountry.processUnitBusiness')) {
      return _.get(jp, 'processCountry.processUnitBusiness.unitBusiness');
    }
  }

  associatePaiToJob(jobPai: JobPai): Observable<any[]> {
    return this.http.post<any[]>(this.environment.base_URL + "/job-pai", jobPai);
  }

  rejectJob(jobOS: JobOS): Observable<any[]> {
    return this.http.post<any[]>(this.environment.base_URL + "/job-os", jobOS);
  }

  getJobsSelect(financialYear:any = null, cache = true): Observable<any> {
    let cacheKey = "jobSelected";
    let params: any = {}
    params.select = true;
    
    if (_.includes(this.router.url, 'times/imputation')) {
      params.financialYear = financialYear.join(',');
    }
    if(params.financialYear){
      cacheKey= cacheKey+"_"+financialYear.toString();
    }

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(this.environment.base_URL + `/jobs`, { params }).pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobsByOS(os: any, financialYear: any = null, showRecommendation: any = null, cache = true, cacheKey = "jobByOS"): Observable<any> {
    let params: any = {}
    if (os) {
      cacheKey= cacheKey+"_"+os.toString();
      params = {
        OSId: os
      }
      if (financialYear) {
        cacheKey= cacheKey+"_"+financialYear.toString();
        params.financialYear = financialYear.join(',');

      }

      if (showRecommendation) {
        cacheKey= cacheKey+"_"+showRecommendation.toString();
        params.showRecommendation = showRecommendation;
      }
    }
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(this.environment.base_URL + `/jobs`, { params }).pipe(map((res) => {
      this.cacheService.updateCacheData(res, cacheKey)
      return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
    
  }

  //!NO SE USA
  getJobsByEntity(os: any, financialYear: any = null, entities, showRecommendation: any = null): Observable<any> {
    return this.getJobsByOS(os, financialYear, showRecommendation).pipe(map((res: any) => {

      const jobs = _.filter(res, (job: any) =>
        _.find(job.entities, entity => _.map(entities, item => _.isEqual(item, entity)))
      );
      return jobs;
    }));
  }

  getResponsable(job) {
    let responsable = null;
    _.forEach(_.get(job, 'users'), (user) => {
      if (_.get(user, 'responsible')) {
        responsable = user;
      }
    })
    return responsable;
  }

  importProgramJob(jobId, sourceJobId): Observable<any[]> {
    let params = {
      sourceJobId
    }
    return this.http.post<any[]>(this.environment.base_URL + `/program-job/import/${jobId}`, null, { params });
  }


  getFilteredJobs(params): Observable<any[]> {
    return this.http.get<any[]>(this.environment.base_URL + "/filtered-jobs", { params });
  }


  getJobsThatHaveProgramJob(): Observable<any> {
    let params: any = {}
    params.hasProgramJob = true;
    return this.http.get(this.environment.base_URL + `/jobs`, { params });
  }

  deleteCache() {

    let memoryCache = ["usersByJob"];

    _.forEach(memoryCache, item => {
      this.cacheService.deleteCache(item);
    })

  }

}
